import { AlertTriangleIcon, RotateCwIcon } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui'
import type { LedgerIncomplete } from '@/modules/projects/stores/project/types'
import { captureException } from '@/services/monitoring'
import { useLedgerGap } from '../usePortfolioOverview'

export const ledgerIncompleteSentence = ({ page }: LedgerIncomplete) =>
  `Cash ledger incomplete: page ${page} failed to load. Totals may be short.`

interface LedgerIncompleteNoticeViewProps {
  ledgerIncomplete: LedgerIncomplete
  isRetrying: boolean
  onRetry: () => void
}

export const LedgerIncompleteNoticeView = ({
  ledgerIncomplete,
  isRetrying,
  onRetry,
}: LedgerIncompleteNoticeViewProps) => (
  <div
    role="alert"
    className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
  >
    <span className="flex items-center gap-2">
      <AlertTriangleIcon className="size-4 shrink-0 text-amber-600" />
      {ledgerIncompleteSentence(ledgerIncomplete)}
    </span>
    <Button
      theme="outline"
      size="sm"
      className="h-7 cursor-pointer font-medium text-blue-7"
      disabled={isRetrying}
      onClick={onRetry}
    >
      <RotateCwIcon />
      Retry
    </Button>
  </div>
)

// Says the ledger the totals add up is short, and loads the failed page again on Retry.
export const LedgerIncompleteNotice = () => {
  const { ledgerIncomplete, retry } = useLedgerGap()
  const [isRetrying, setIsRetrying] = useState(false)
  if (!ledgerIncomplete) return null

  // A retry that fails again records the new failed page, so the notice stays and names it.
  const onRetry = () => {
    setIsRetrying(true)
    retry()
      .catch((error: Error) => captureException(error))
      .finally(() => setIsRetrying(false))
  }

  return (
    <LedgerIncompleteNoticeView
      ledgerIncomplete={ledgerIncomplete}
      isRetrying={isRetrying}
      onRetry={onRetry}
    />
  )
}
